import {
  Component,
  forwardRef,
  ChangeDetectionStrategy,
  input,
  output,
  signal,
} from '@angular/core';

import {
  NG_VALUE_ACCESSOR,
  ControlValueAccessor,
  FormsModule,
  ReactiveFormsModule,
} from '@angular/forms';
import { MatSelectModule } from '@angular/material/select';
import { MatOptionModule } from '@angular/material/core';

import { InputWrapperComponent } from '../input-wrapper/input-wrapper.component';
import { IconName } from 'src/app/components/icon/icons/icon-map';

@Component({
  selector: 'app-select',
  imports: [
    FormsModule,
    ReactiveFormsModule,
    MatSelectModule,
    MatOptionModule,
    InputWrapperComponent
],
  templateUrl: './select.component.html',
  styleUrls: ['./select.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => SelectComponent),
      multi: true,
    },
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SelectComponent<T = any> implements ControlValueAccessor {
  readonly id = input<string | undefined>(undefined);

  readonly titleLabel = input<string | undefined>(undefined);
  readonly titleIcon = input<IconName | undefined>(undefined);
  readonly infoLabel = input<string | undefined>(undefined);
  readonly style = input<string | undefined>(undefined);
  readonly appearance = input<'outline' | 'fill' | 'standard' | 'legacy'>('outline');
  readonly placeholder = input<string | undefined>(undefined);
  readonly options = input<T[]>([]);
  readonly optionLabelFn = input<((option: T) => string) | undefined>(undefined);
  readonly isLoading = input(false);
  readonly loadingText = input($localize`:@@loading:Carregando...`);
  readonly titleIconClick = output<void>();

  disabled = signal(false);
  value = signal<T | null>(null);

  get displayOptions(): T[] {
    return this.options();
  }

  onChange: (value: T | null) => void = () => {};
  onTouched: () => void = () => {};

  getOptionLabel(option: T): string {
    if ((option as any)?.__loading) {
      return this.loadingText();
    }
    const fn = this.optionLabelFn();
    return fn ? fn(option) : ((option as any)?.name ?? String(option));
  }

  onSelect(val: T) {
    this.value.set(val);
    this.onChange(val);
    this.onTouched();
  }

  writeValue(val: T | null): void {
    this.value.set(val);
  }

  registerOnChange(fn: (value: T | null) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled.set(isDisabled);
  }
}
